import styled from '@emotion/styled';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { i18n, openCallMeModal, ProductTypeCodeEnum, RenewalOf, RootState, setCustomer, setFlowType, setProgressBarPercentage, setSoId, setSoIdWeb, useAppDispatch, useAppSelector } from 'src/common';
import { CalculationResponseDto, useGetCalculationsQuery, useGetSoQuery } from 'src/common/api/apollo/additional';
import { BackLink, Flex, Loader, RequestAgentCardV2, Text } from 'src/components';
import { CalculationAdditionalCard } from 'src/components/cards/additional';
import { colors } from 'src/theme';
import { AppRoutesAdditional, renewalEntryPath } from '../../_router/app.routes';

const SubtitleStyle = styled(Text)({
  lineHeight: '24px',
});

const OffersAdditionalPreviewPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{ soId: string }>();
  const soIdWeb = useAppSelector((state: RootState) => state.model.offer.soIdWeb);

  const customerData = useAppSelector((state: RootState) => state.model.customer.data);

  const [dateOfBirth, setDateOfBirth] = useState<string | undefined>(params.soId ? undefined : customerData.birthDate);
  /** Selfcare renewal (health-api `renewalOf` on the SO): pins the previous package and changes the back link. */
  const [renewalOf, setRenewalOf] = useState<RenewalOf | null>(null);
  const isRenewal = renewalOf?.productTypeCode === 'dodatno';

  const navigateToNoOffer = () => {
    navigate(AppRoutesAdditional.NO_OFFER.fullPath);
  }

  const { data: getCalculationsData, loading: loadingCalculations } = useGetCalculationsQuery({
    variables: { input: { birthDate: dateOfBirth || '', type: ProductTypeCodeEnum.DODATNO, coverages: [] } },
    skip: !dateOfBirth,
    onError: () => {
      navigateToNoOffer();
    }
  });

  useGetSoQuery({
    variables: { id: params.soId || '' },
    skip: !!dateOfBirth && !params.soId,
    onCompleted: (result) => {
      const soRenewalOf = (result.getSo?.productTypeSpecific?.renewalOf ?? null) as RenewalOf | null;
      setRenewalOf(soRenewalOf);
      dispatch(setCustomer({
        birthDate: result.getSo?.productTypeSpecific?.insuredPerson?.birthDate ?? '',
        age: result.getSo?.productTypeSpecific?.insuredPerson?.birthDate ? dayjs().year() - dayjs(result.getSo?.productTypeSpecific?.insuredPerson?.birthDate).year() : undefined,
        firstName: result.getSo?.productTypeSpecific?.insuredPerson?.firstName ?? '',
        lastName: result.getSo?.productTypeSpecific?.insuredPerson?.lastName ?? '',
        ...(soRenewalOf ? { email: result.getSo?.email ?? '', phoneNumber: result.getSo?.phoneNumber ?? '', hasPolicy: true } : {}),
      }));
      dispatch(setSoIdWeb(result.getSo?._id));
      dispatch(setSoId(result.getSo?._id));
      setDateOfBirth(result.getSo?.productTypeSpecific?.insuredPerson?.birthDate ?? '');
    },
    onError: () => {
      navigateToNoOffer();
    }
  });

  useEffect(() => {
    dispatch(setProgressBarPercentage(45));
    dispatch(setFlowType('web'));
  }, []);

  if (loadingCalculations) return <Loader page />;

  if (!params.soId && !soIdWeb) {
    return <Loader page />;
  }

  // Rule 5: the offer of the same insurer / package first, the rest below for comparison.
  const isPreviousPackage = (calculation: CalculationResponseDto) =>
    isRenewal && !!renewalOf?.productCode && calculation.code === renewalOf.productCode;
  const calculations = [...(getCalculationsData?.getCalculations ?? [])].sort(
    (a, b) => Number(isPreviousPackage(b)) - Number(isPreviousPackage(a)),
  );
  const backPath = isRenewal && (params.soId || soIdWeb)
    ? renewalEntryPath(params.soId || soIdWeb || '')
    : AppRoutesAdditional.CUSTOMER_DATA.fullPath;

  return (
    <Flex vertical gap={24}>
      <Flex vertical gap={8}>
        <Text type="large" bold>
          {i18n.t(isRenewal ? 'pages.offersAdditionalPage.renewalTitle' : 'pages.offersAdditionalPage.title')}
        </Text>
        <SubtitleStyle type="small" color={colors.text3}>{i18n.t(isRenewal ? 'pages.offersAdditionalPage.renewalDescription' : 'pages.offersAdditionalPage.description')}</SubtitleStyle>
      </Flex>

      {calculations.map((calculation: CalculationResponseDto) => {
        const { premiumEligibleAgeMin, premiumEligibleAgeMax } = calculation;
        const offerSubDescription =
          typeof premiumEligibleAgeMax === 'number' && premiumEligibleAgeMax > 100
            ? i18n.t('pages.offersAdditionalPage.offerSubDescriptionFromAge', { premiumEligibleAgeMin })
            : i18n.t('pages.offersAdditionalPage.offerSubDescription', { premiumEligibleAgeMin, premiumEligibleAgeMax });

        return (
          <CalculationAdditionalCard
            soId={params.soId || soIdWeb || ''}
            key={calculation.code}
            calculation={calculation}
            showSelectButton
            onSelectOffer={() => { }}
            offerSubDescription={offerSubDescription}
            selectButtonText={i18n.t('pages.offersAdditionalPage.selectButton')}
            highlightLabel={isPreviousPackage(calculation) ? i18n.t('pages.offersAdditionalPage.currentPackage') : undefined}
          />
        );
      })}

      <RequestAgentCardV2 onButtonClick={() => dispatch(openCallMeModal())} />

      <Flex justify="center">
        <BackLink text={i18n.t('pages.offersAdditionalPage.backLink')} path={backPath} />
      </Flex>
    </Flex>
  );
};

export default OffersAdditionalPreviewPage;
